<template>
    <div class="media-draggable" :class="{ 'media-draggable--inline-actions': inlineActions }">
        <div
            class="tw:flex tw:w-full"
            :class="
                inlineActions
                    ? 'tw:flex-row tw:flex-nowrap tw:items-center tw:xl:gap-0'
                    : 'tw:flex-col tw:xl:flex-row'
            "
            data-testid="media-draggable-layout"
        >
            <div class="level-item">
                <div
                    v-if="!hasPrerequisite && !isAPrerequisite"
                    class="media-draggable__draghandle"
                    :class="{ 'is-disabled': disabled }"
                    @focus="hoverIconHidden = true"
                    @focusout="hoverIconHidden = false"
                    @mouseover="hoverIconHidden = true"
                    @mouseleave="hoverIconHidden = false"
                >
                    <e-icon v-if="hoverIcon && !hoverIconHidden" :icon="hoverIcon" />
                    <e-icon v-if="!hoverIcon || hoverIconHidden" icon="draggabledots" />
                </div>

                <div v-else class="is-disabled">
                    <e-tooltip
                        label="Dragging is disabled. Remove prerequisite to enable dragging"
                        position="is-right"
                        type="is-warning"
                        multilined
                    >
                        <e-icon icon="lock" />
                    </e-tooltip>
                </div>
            </div>

            <div
                class="level-item media-draggable__title tw:flex tw:flex-col tw:min-w-0"
                :class="{
                    'is-clickable': canOpen,
                    'tw:flex-1 tw:text-left tw:min-w-0 tw:shrink!': inlineActions,
                }"
                :role="ownsToggle ? 'button' : undefined"
                :tabindex="ownsToggle ? 0 : undefined"
                :aria-expanded="ownsToggle ? open : undefined"
                @click="toggle"
                @keydown.enter.self.prevent="onToggleKeydown"
                @keydown.space.self.prevent="onToggleKeydown"
            >
                <div class="tw:flex tw:min-w-0" :class="{ 'tw:items-center': inlineActions }">
                    <slot name="title" />
                    <e-icon
                        v-if="canOpen"
                        :icon="`angle-${open ? 'up' : 'down'}`"
                        class="has-fast-transition has-fast-transition-on-hover media-draggable__icon"
                    />
                </div>
            </div>

            <slot name="actions" />
        </div>
    </div>
</template>

<script>
export default {
    name: 'MediaDraggable',

    data() {
        return {
            hoverIconHidden: false,
        };
    },

    props: {
        // null default so ownsToggle is false unless a real callback is passed
        // (e.g. AccordionHeader already owns keyboard/toggle for rubric criteria).
        toggleCallback: {
            type: Function,
            default: null,
        },
        hoverIcon: {
            type: String,
            default: null,
        },
        open: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        canOpen: {
            type: Boolean,
            default: true,
        },
        hasPrerequisite: {
            type: Boolean,
            default: false,
        },
        isAPrerequisite: {
            type: Boolean,
            default: false,
        },
        // Keep title + actions on one row at tablet widths (including 768px iPad Mini).
        inlineActions: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        dragClasses() {
            return {
                'media-draggable__draghandle': !this.hasPrerequisite,
                'is-disabled': this.disabled,
            };
        },
        ownsToggle() {
            return this.canOpen && typeof this.toggleCallback === 'function';
        },
    },

    methods: {
        toggle() {
            if (!this.canOpen) return;

            this.toggleCallback?.();
        },
        onToggleKeydown() {
            if (!this.ownsToggle) return;

            this.toggle();
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.media-draggable {
    position: relative;

    .level {
        justify-content: flex-start;

        @include until($tablet) {
            display: flex;
            flex-direction: column;
        }

        .level-item {
            flex-grow: 0 !important;
        }
    }

    &:hover,
    &:active {
        color: $primary;
        transition: $transition-fast;
    }

    &__draghandle {
        font-size: $size-4;
        margin-top: -$size-10;
        transition: $transition-fast;

        @include from($tablet) {
            margin-top: 0;
            padding-right: $size-2;
        }

        &:hover {
            cursor: grab;
        }

        &:active {
            cursor: grabbing;
        }

        &::after {
            background: $border;
            content: '';
            display: block;
            height: 1px;
            position: absolute;
            left: 0;
            right: 0;
            top: calc(#{$card-header-padding-horizontal * 1.3} + 24px);
            width: 100%;

            @include from($tablet) {
                background: $border;
                content: '';
                display: block;
                height: 100%;
                left: calc(#{$card-header-padding-horizontal * 2} + 24px);
                position: absolute;
                right: auto;
                top: 0;
                width: 1px;
            }
        }
    }

    &__icon {
        margin-left: 0.5rem;
        vertical-align: middle;
    }

    &__title {
        flex-grow: 2;
        text-align: center;
        text-overflow: ellipsis;
        transition: $transition-fast;

        @include from($tablet) {
            margin-right: auto !important;
            text-align: left;
        }

        &:hover {
            color: $primary;
            transition: $transition-fast;
        }

        & > .capitalize {
            text-transform: capitalize;
        }

        .icon {
            margin-right: $size-9;
        }

        & + .level-item {
            @include from($tablet) {
                margin-left: 0 !important;
            }
        }
    }

    &__actions {
        display: inherit;
        align-items: center;
    }

    // Rubric criteria: keep drag handle, title, and Delete on one aligned row
    // without changing the shared stacked layout other consumers use below xl.
    &--inline-actions {
        .level-item:not(:last-child) {
            margin-bottom: 0;
        }

        .media-draggable__draghandle {
            margin-top: 0;
            padding-right: $size-2;

            &::after {
                height: 100%;
                left: calc(#{$card-header-padding-horizontal * 2} + 24px);
                right: auto;
                top: 0;
                width: 1px;
            }
        }

        .media-draggable__title {
            align-items: center;
            justify-content: flex-start;
            margin-right: auto !important;
            text-align: left;

            p {
                margin: 0;
            }
        }
    }
}
</style>
